import { valuesMatch } from "@/domain/calibration-params";
import type { NvpParameter } from "@/domain/calibration-params";

/** True when the boat holds every one of these params at the stored value, so
 * writing them would change nothing. A param the boat did not answer counts as
 * not held. Values compare at the live param's Accuracy, like every cell. */
export function boatHolds(
  params: readonly string[],
  stored: Map<string, NvpParameter>,
  live: Map<string, NvpParameter>,
): boolean {
  return params.every((name) => {
    const liveParam = live.get(name);
    if (!liveParam || liveParam.Value == null) return false;
    const storedParam = stored.get(name);
    return valuesMatch(
      storedParam?.Value,
      liveParam.Value,
      liveParam.Accuracy ?? storedParam?.Accuracy,
      liveParam.ChpDataType ?? storedParam?.ChpDataType,
    );
  });
}

interface GoldenAtEvent {
  event_name: string;
  fetched_at: string;
}

/** The boat's newest golden at another event, when it is newer than this build's.
 * Goldens order by fetched_at, the same rule the admin sync uses (ADR-026). */
export function newerGoldenThan(
  goldenFetchedAt: string,
  history: readonly GoldenAtEvent[],
): GoldenAtEvent | null {
  const golden = Date.parse(goldenFetchedAt);
  let newest: GoldenAtEvent | null = null;
  for (const entry of history) {
    const fetched = Date.parse(entry.fetched_at);
    if (fetched > golden && (newest === null || fetched > Date.parse(newest.fetched_at))) {
      newest = entry;
    }
  }
  return newest;
}

interface WriteResult {
  boat_code: string;
  success_count: number;
  error_count: number;
}

/** Toast copy for a write result, in the sync card's words. */
export function writeOutcome(result: WriteResult): { ok: boolean; message: string } {
  if (result.error_count > 0) {
    return {
      ok: false,
      message: `Write completed with ${result.error_count} error${result.error_count === 1 ? "" : "s"}`,
    };
  }
  return {
    ok: true,
    message: `Wrote ${result.success_count} parameter${result.success_count === 1 ? "" : "s"} to ${result.boat_code}`,
  };
}

/** The golden column is always this build's golden; naming the event says which
 * cal it is, the way the wing section labels its baseline. */
export function goldenColumnLabel(eventName?: string): string {
  return eventName ? `${eventName} (golden)` : "Golden";
}
